import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { DataTable, DataTableColumn, DateField, EmptyState, PageHeader, StatusChip } from '@routes-plan/ui';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { nowOperationalDateString } from '../../../utils/operationalDateTime';
import { setPageTitle } from '../../../utils/pageTitle';
import { TextKey, useT } from '../../../i18n';
import LocationPicker, { isLocationComplete, toApiLocation } from '../shared/LocationPicker';
import DateRangeOfficeFilter, { RangeFilter, filterParams } from '../shared/DateRangeOfficeFilter';
import {
  BatchRow,
  INVENTORY_ROUTES,
  NamedRef,
  OfficeOption,
  Product,
  STOCK_STATUS_LABEL,
  ShowAlerts,
  StockRow,
  StockStatus,
  WriteOffReason,
  apiErrorMessage,
  fetchOffices,
  fetchStock,
  formatDate,
  locationLabel,
  newRequestId,
} from '../shared/inventoryApi';
import { FieldGrid, FormFooter, IntField, PageBody, PageTabs, SaveButton, Section, fullRow, useInventoryAlert } from '../shared/ui';

type Loc = { officeId: number | null; routeId: number | null };
type Errors = Partial<Record<'location' | 'product' | 'quantity' | 'reason', string>>;

/** Etiqueta traducida del estado de stock (DISPONIBLE / NO_PRESTABLE). */
const stockStatusKey = (status: string) => `writeOffs.stockStatus.${status}` as TextKey;

/** Bajas (vencido, dañado, garantía…), apartar unidades como no prestables y su historial. */
const WriteOffs: React.FC<ShowAlerts> = () => {
  const t = useT();
  const { canCreate, canUpdate, canRead, isLoadingPermissions } = useResourcePermissions(INVENTORY_ROUTES.writeOffs);
  const tabs = [
    { key: 'baja', label: t('writeOffs.tabWriteOff'), allowed: canCreate },
    { key: 'estado', label: t('writeOffs.tabStatus'), allowed: canUpdate },
    { key: 'historial', label: t('writeOffs.tabHistory'), allowed: canRead },
  ].filter((tb) => tb.allowed);
  const [tab, setTab] = useState(0);
  setPageTitle('writeOffs.pageTitle');
  const index = Math.min(tab, Math.max(0, tabs.length - 1));
  const current = tabs[index];

  return (
    <PageBody>
      <PageHeader title={t('writeOffs.title')} description={t('writeOffs.description')} />
      {tabs.length === 0 ? (
        isLoadingPermissions ? null : <EmptyState icon={<LockOutlinedIcon fontSize="inherit" />} title={t('writeOffs.noSectionPermission')} />
      ) : (
        <>
          <PageTabs value={index} onChange={setTab} labels={tabs.map((tb) => tb.label)} />
          {current?.key === 'baja' && <WriteOffForm />}
          {current?.key === 'estado' && <StatusChangeForm />}
          {current?.key === 'historial' && <WriteOffHistory />}
        </>
      )}
    </PageBody>
  );
};

/** Carga stock (con lotes) de la ubicación elegida. */
const useLocationStock = (location: Loc, showAlert: ReturnType<typeof useInventoryAlert>) => {
  const t = useT();
  const [stock, setStock] = useState<StockRow[]>([]);
  const [batches, setBatches] = useState<BatchRow[]>([]);
  const reload = useCallback(async () => {
    if (!isLocationComplete(location)) {
      setStock([]);
      setBatches([]);
      return;
    }
    try {
      const { data, batches: b } = await fetchStock(toApiLocation(location), true);
      setStock(data);
      setBatches(b);
    } catch (error) {
      showAlert(apiErrorMessage(error, t('writeOffs.stockLoadError')), 'error');
    }
  }, [location, showAlert, t]);
  useEffect(() => {
    reload();
  }, [reload]);
  return { stock, batches, reload };
};

const useOffices = () => {
  const [offices, setOffices] = useState<OfficeOption[]>([]);
  useEffect(() => {
    fetchOffices().then(setOffices).catch(() => setOffices([]));
  }, []);
  return offices;
};

const ErrorLine: React.FC<{ text?: string }> = ({ text }) =>
  text ? (
    <Typography variant="caption" color="error" role="alert" sx={{ mt: -1 }}>
      {text}
    </Typography>
  ) : null;

const WriteOffForm: React.FC = () => {
  const t = useT();
  const showAlert = useInventoryAlert();
  const { canCreate } = useResourcePermissions(INVENTORY_ROUTES.writeOffs);
  const offices = useOffices();
  const [location, setLocation] = useState<Loc>({ officeId: null, routeId: null });
  const { stock, batches, reload } = useLocationStock(location, showAlert);
  const [reasons, setReasons] = useState<WriteOffReason[]>([]);
  const [expirationById, setExpirationById] = useState<Map<number, boolean>>(new Map());
  const [productId, setProductId] = useState<number | ''>('');
  const [status, setStatus] = useState<StockStatus>('DISPONIBLE');
  const [batchId, setBatchId] = useState<number | ''>('');
  const [quantity, setQuantity] = useState('');
  const [reasonId, setReasonId] = useState<number | ''>('');
  const [observation, setObservation] = useState('');
  const [date, setDate] = useState(nowOperationalDateString());
  const [errors, setErrors] = useState<Errors>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get('write-off-reasons').then(({ data }) => setReasons(Array.isArray(data?.data) ? data.data : [])).catch(() => setReasons([]));
    axios
      .get('products')
      .then(({ data }) => {
        const list: Product[] = Array.isArray(data?.data) ? data.data : [];
        setExpirationById(new Map(list.map((p) => [p.id, Boolean(p.hasExpiration)])));
      })
      .catch(() => setExpirationById(new Map()));
  }, []);

  const row = productId !== '' ? stock.find((s) => s.productId === productId) : undefined;
  const max = row ? (status === 'DISPONIBLE' ? row.available : row.notLendable) : 0;
  const productBatches = batches.filter((b) => b.productId === productId && b.status === status && b.quantity > 0);
  // VENCIDO (requiresExpiration) solo para productos que manejan vencimiento.
  const allowedReasons = useMemo(
    () => reasons.filter((r) => !r.requiresExpiration || (productId !== '' && expirationById.get(Number(productId)))),
    [reasons, productId, expirationById],
  );

  const save = async () => {
    if (!canCreate) return;
    const next: Errors = {};
    if (!isLocationComplete(location)) next.location = t('writeOffs.errLocation');
    if (productId === '') next.product = t('writeOffs.errProduct');
    if (!(Number(quantity) > 0)) next.quantity = t('writeOffs.errQuantity');
    if (reasonId === '') next.reason = t('writeOffs.errReason');
    setErrors(next);
    if (Object.keys(next).length) return;
    setSaving(true);
    try {
      await axios.post('write-offs', {
        ...toApiLocation(location),
        date,
        productId: productId,
        quantity: Number(quantity),
        stockStatus: status,
        reasonId: reasonId,
        observation: observation || null,
        clientRequestId: newRequestId('writeoff'),
        ...(batchId !== '' ? { batchId: batchId } : {}),
      });
      showAlert(t('writeOffs.writeOffSaved'), 'success');
      setQuantity('');
      setObservation('');
      setBatchId('');
      await reload();
    } catch (error) {
      showAlert(apiErrorMessage(error, t('writeOffs.writeOffError')), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title={t('writeOffs.tabWriteOff')} description={t('writeOffs.writeOffHelp')}>
      <Box sx={{ display: 'grid', gap: 2, maxWidth: 1100 }}>
        <LocationPicker
          offices={offices}
          value={location}
          onChange={(v) => {
            setLocation(v);
            setProductId('');
            setErrors((e) => ({ ...e, location: undefined }));
          }}
          disabled={!canCreate}
          size="medium"
        />
        <ErrorLine text={errors.location} />
        <FieldGrid md={3}>
          <TextField
            select
            fullWidth
            required
            label={t('writeOffs.product')}
            value={productId === '' ? '' : String(productId)}
            onChange={(e) => {
              setProductId(Number(e.target.value) || '');
              setBatchId('');
              setReasonId('');
              setErrors((x) => ({ ...x, product: undefined }));
            }}
            disabled={!canCreate || stock.length === 0}
            error={Boolean(errors.product)}
            helperText={errors.product ?? (isLocationComplete(location) && stock.length === 0 ? t('writeOffs.noStockHere') : undefined)}
            sx={{ gridColumn: { md: 'span 2' } }}
          >
            {stock
              .filter((s) => s.available > 0 || s.notLendable > 0)
              .map((s) => (
                <MenuItem key={s.productId} value={String(s.productId)}>
                  {s.title} · {t('writeOffs.availableShort', { n: s.available })}
                  {s.notLendable ? ` · ${t('writeOffs.notLendableShort', { n: s.notLendable })}` : ''}
                </MenuItem>
              ))}
          </TextField>
          <TextField
            select
            fullWidth
            label={t('writeOffs.from')}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as StockStatus);
              setBatchId('');
            }}
            disabled={!canCreate}
          >
            {(Object.keys(STOCK_STATUS_LABEL) as StockStatus[]).map((st) => (
              <MenuItem key={st} value={st}>
                {t(stockStatusKey(st))}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label={t('writeOffs.lot')}
            value={batchId === '' ? '' : String(batchId)}
            onChange={(e) => setBatchId(Number(e.target.value) || '')}
            disabled={!canCreate || productId === ''}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">{t('writeOffs.lotAutoFirstExpiring')}</MenuItem>
            {productBatches.map((b) => (
              <MenuItem key={b.batchId} value={String(b.batchId)}>
                {b.lotCode || t('writeOffs.lotN', { id: b.batchId })}
                {b.expirationDate ? ` · ${t('writeOffs.expires', { date: formatDate(b.expirationDate) })}` : ''} · {b.quantity}
              </MenuItem>
            ))}
          </TextField>
          <IntField
            fullWidth
            required
            label={t('writeOffs.quantity')}
            value={quantity}
            onChange={(v) => {
              setQuantity(v);
              setErrors((x) => ({ ...x, quantity: undefined }));
            }}
            disabled={!canCreate}
            error={Boolean(errors.quantity) || (row ? Number(quantity) > max : false)}
            helperText={errors.quantity ?? (row ? t('writeOffs.max', { n: max }) : undefined)}
          />
          <TextField
            select
            fullWidth
            required
            label={t('writeOffs.reason')}
            value={reasonId === '' ? '' : String(reasonId)}
            onChange={(e) => {
              setReasonId(Number(e.target.value) || '');
              setErrors((x) => ({ ...x, reason: undefined }));
            }}
            disabled={!canCreate || productId === ''}
            error={Boolean(errors.reason)}
            helperText={errors.reason}
          >
            {allowedReasons.map((r) => (
              <MenuItem key={r.id} value={String(r.id)}>
                {r.name}
              </MenuItem>
            ))}
          </TextField>
          <DateField fullWidth label={t('common.date')} value={date || null} onChange={(v) => setDate(v ?? '')} disabled={!canCreate} />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={t('writeOffs.observation')}
            value={observation}
            onChange={(e) => setObservation(e.target.value)}
            disabled={!canCreate}
            sx={fullRow}
          />
        </FieldGrid>
        <FormFooter>
          <SaveButton allowed={canCreate} deniedTooltip={t('writeOffs.noPermissionWriteOff')} color="error" loading={saving} onClick={save} startIcon={<DeleteSweepOutlinedIcon />}>
            {t('writeOffs.tabWriteOff')}
          </SaveButton>
        </FormFooter>
      </Box>
    </Section>
  );
};

const StatusChangeForm: React.FC = () => {
  const t = useT();
  const showAlert = useInventoryAlert();
  const { canUpdate } = useResourcePermissions(INVENTORY_ROUTES.writeOffs);
  const offices = useOffices();
  const [location, setLocation] = useState<Loc>({ officeId: null, routeId: null });
  const { stock, batches, reload } = useLocationStock(location, showAlert);
  const [productId, setProductId] = useState<number | ''>('');
  const [direction, setDirection] = useState<'apartar' | 'liberar'>('apartar');
  const [batchId, setBatchId] = useState<number | ''>('');
  const [quantity, setQuantity] = useState('');
  const [observation, setObservation] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [saving, setSaving] = useState(false);
  const from: StockStatus = direction === 'apartar' ? 'DISPONIBLE' : 'NO_PRESTABLE';
  const to: StockStatus = direction === 'apartar' ? 'NO_PRESTABLE' : 'DISPONIBLE';
  const row = productId !== '' ? stock.find((s) => s.productId === productId) : undefined;
  const max = row ? (from === 'DISPONIBLE' ? row.available : row.notLendable) : 0;

  const save = async () => {
    if (!canUpdate) return;
    const next: Errors = {};
    if (!isLocationComplete(location)) next.location = t('writeOffs.errLocation');
    if (productId === '') next.product = t('writeOffs.errProduct');
    if (!(Number(quantity) > 0)) next.quantity = t('writeOffs.errQuantity');
    setErrors(next);
    if (Object.keys(next).length) return;
    setSaving(true);
    try {
      await axios.post('inventory/status-changes', {
        ...toApiLocation(location),
        productId: productId,
        quantity: Number(quantity),
        from,
        to,
        observation: observation || null,
        ...(batchId !== '' ? { batchId: batchId } : {}),
      });
      showAlert(direction === 'apartar' ? t('writeOffs.setAsideOk') : t('writeOffs.releasedOk'), 'success');
      setQuantity('');
      setObservation('');
      await reload();
    } catch (error) {
      showAlert(apiErrorMessage(error, t('writeOffs.statusError')), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title={t('writeOffs.tabStatus')} description={t('writeOffs.statusHelp')}>
      <Box sx={{ display: 'grid', gap: 2, maxWidth: 1100 }}>
        <LocationPicker
          offices={offices}
          value={location}
          onChange={(v) => {
            setLocation(v);
            setProductId('');
            setErrors((e) => ({ ...e, location: undefined }));
          }}
          disabled={!canUpdate}
          size="medium"
        />
        <ErrorLine text={errors.location} />
        <FieldGrid md={3}>
          <TextField
            select
            fullWidth
            label={t('writeOffs.action')}
            value={direction}
            onChange={(e) => {
              setDirection(e.target.value as 'apartar' | 'liberar');
              setBatchId('');
              setProductId('');
            }}
            disabled={!canUpdate}
          >
            <MenuItem value="apartar">{t('writeOffs.setAside')}</MenuItem>
            <MenuItem value="liberar">{t('writeOffs.release')}</MenuItem>
          </TextField>
          <TextField
            select
            fullWidth
            required
            label={t('writeOffs.product')}
            value={productId === '' ? '' : String(productId)}
            onChange={(e) => {
              setProductId(Number(e.target.value) || '');
              setBatchId('');
              setErrors((x) => ({ ...x, product: undefined }));
            }}
            disabled={!canUpdate || stock.length === 0}
            error={Boolean(errors.product)}
            helperText={errors.product}
            sx={{ gridColumn: { md: 'span 2' } }}
          >
            {stock
              .filter((s) => (from === 'DISPONIBLE' ? s.available : s.notLendable) > 0)
              .map((s) => (
                <MenuItem key={s.productId} value={String(s.productId)}>
                  {s.title} · {from === 'DISPONIBLE' ? s.available : s.notLendable}
                </MenuItem>
              ))}
          </TextField>
          <TextField
            select
            fullWidth
            label={t('writeOffs.lot')}
            value={batchId === '' ? '' : String(batchId)}
            onChange={(e) => setBatchId(Number(e.target.value) || '')}
            disabled={!canUpdate || productId === ''}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">{t('writeOffs.lotAuto')}</MenuItem>
            {batches
              .filter((b) => b.productId === productId && b.status === from && b.quantity > 0)
              .map((b) => (
                <MenuItem key={b.batchId} value={String(b.batchId)}>
                  {b.lotCode || t('writeOffs.lotN', { id: b.batchId })} · {b.quantity}
                </MenuItem>
              ))}
          </TextField>
          <IntField
            fullWidth
            required
            label={t('writeOffs.quantity')}
            value={quantity}
            onChange={(v) => {
              setQuantity(v);
              setErrors((x) => ({ ...x, quantity: undefined }));
            }}
            disabled={!canUpdate}
            error={Boolean(errors.quantity) || (row ? Number(quantity) > max : false)}
            helperText={errors.quantity ?? (row ? t('writeOffs.max', { n: max }) : undefined)}
          />
          <TextField fullWidth label={t('writeOffs.observation')} value={observation} onChange={(e) => setObservation(e.target.value)} disabled={!canUpdate} />
        </FieldGrid>
        <FormFooter>
          <SaveButton allowed={canUpdate} deniedTooltip={t('writeOffs.noPermissionStatus')} loading={saving} onClick={save}>
            {direction === 'apartar' ? t('writeOffs.setAsideButton') : t('writeOffs.releaseButton')}
          </SaveButton>
        </FormFooter>
      </Box>
    </Section>
  );
};

type WriteOffRow = {
  id: number;
  date: string;
  office?: NamedRef | null;
  route?: NamedRef | null;
  product?: { title?: string } | null;
  quantity: number;
  stockStatus: string;
  reason?: { name?: string } | null;
  batch?: { id: number; lotCode?: string | null } | null;
  observation?: string | null;
};

const WriteOffHistory: React.FC = () => {
  const t = useT();
  const showAlert = useInventoryAlert();
  const offices = useOffices();
  const [filter, setFilter] = useState<RangeFilter>({ startDate: `${nowOperationalDateString().slice(0, 8)}01`, endDate: nowOperationalDateString(), officeId: '' });
  const [rows, setRows] = useState<WriteOffRow[]>([]);
  const [loading, setLoading] = useState(true);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('write-offs', { params: filterParams(filter) });
      setRows(Array.isArray(data?.data) ? data.data : []);
    } catch (error) {
      showAlert(apiErrorMessage(error, t('writeOffs.historyError')), 'error');
    } finally {
      setLoading(false);
    }
  }, [filter, showAlert, t]);

  useEffect(() => {
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusLabel = (s: string) => (STOCK_STATUS_LABEL[s as StockStatus] ? t(stockStatusKey(s)) : s);
  const columns: DataTableColumn<WriteOffRow>[] = [
    { id: 'date', header: t('common.date'), value: (r) => formatDate(r.date), sortable: true },
    { id: 'product', header: t('writeOffs.product'), value: (r) => r.product?.title ?? '', sortable: true },
    { id: 'location', header: t('writeOffs.location'), value: (r) => locationLabel(r), sortable: true, hideBelow: 'sm' },
    { id: 'quantity', header: t('writeOffs.quantity'), align: 'right', value: (r) => Number(r.quantity), sortable: true },
    {
      id: 'status',
      header: t('writeOffs.from'),
      value: (r) => statusLabel(r.stockStatus),
      render: (r) => <StatusChip label={statusLabel(r.stockStatus)} tone={r.stockStatus === 'DISPONIBLE' ? 'success' : 'warning'} />,
    },
    { id: 'reason', header: t('writeOffs.reason'), value: (r) => r.reason?.name ?? '', sortable: true },
    { id: 'lot', header: t('writeOffs.lot'), value: (r) => (r.batch ? r.batch.lotCode || t('writeOffs.lotN', { id: r.batch.id }) : ''), hideBelow: 'md' },
    { id: 'observation', header: t('writeOffs.observation'), value: (r) => r.observation ?? '', wrap: true, hideBelow: 'md' },
  ];

  return (
    <>
      <Card>
        <CardContent>
          <DateRangeOfficeFilter offices={offices} value={filter} onChange={setFilter} onSearch={search} loading={loading} />
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <DataTable<WriteOffRow>
            rows={rows}
            columns={columns}
            getRowId={(r) => r.id}
            loading={loading}
            ariaLabel={t('writeOffs.tabHistory')}
            exportFileName={t('writeOffs.exportFile')}
            initialSort={{ columnId: 'date', direction: 'desc' }}
            emptyState={<EmptyState icon={<DeleteSweepOutlinedIcon fontSize="inherit" />} title={t('writeOffs.empty')} description={t('writeOffs.emptyHint')} />}
          />
        </CardContent>
      </Card>
    </>
  );
};

export default WriteOffs;
